import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { getItem } from '../../../utils/localStorage';
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import styles from "./invoice.module.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Layout from "../component/layout";
import {
  getCompanyInformationURL,
  getBankDetailsForLevyURL,
  getBillerAddressForLevyURL,
  getFacilityPaymentDetailsURL,
} from '../../../utils/urls';
import { useFacilityStore } from '../../../app/z-store/store';
import CombinedInvoiceActionsDropdown from "./CombinedInvoiceActionsDropdown";

const CombinedInvoicePage = () => {
  // State management
  const routerLocation = useLocation();
  const [combinedInvoice, setCombinedInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReceiptsDialog, setShowReceiptsDialog] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [facilityLogo, setFacilityLogo] = useState("");
  const [bankDetails, setBankDetails] = useState(null);
  const [billerAddress, setBillerAddress] = useState(null);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);

  // Individual loading states
  const [billerAddressLoading, setBillerAddressLoading] = useState(false);
  const [bankDetailsLoading, setBankDetailsLoading] = useState(false);
  const [paymentSettingsLoading, setPaymentSettingsLoading] = useState(false);

  // Hooks
  const { combinedInvoiceId } = useParams();
  const navigate = useNavigate();
  const facilityId = useFacilityStore((state) => state.facilityId);

  // Check if all payment data has loaded
  const allPaymentDataLoaded =
    !billerAddressLoading &&
    !bankDetailsLoading &&
    !paymentSettingsLoading;

  // Fetch data on component mount
  useEffect(() => {
    fetchCombinedInvoiceData();
    fetchCompanyDetails();
    getFacilityLogo();
  }, [combinedInvoiceId, facilityId]);

  // Fetch payment details when invoice is loaded
  useEffect(() => {
    if (combinedInvoice) {
      fetchPaymentDetails();
    }
  }, [combinedInvoice]);

  // Fetch facility logo
  const toDataUrl = async (url) => {
    try {
      const res = await fetch(url, { mode: "cors", cache: "no-cache" });
      if (!res.ok) return url;
      const blob = await res.blob();
      if (!blob.type.startsWith("image/")) return url;
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      return url;
    }
  };

  // Logo comes from the invoice response (inlined so PDF capture isn't blocked by CORS)
  useEffect(() => {
    const logo = combinedInvoice?.facilityInfo?.logo;
    if (combinedInvoice?.facilityInfo?.logoDataUrl) {
      setFacilityLogo(combinedInvoice.facilityInfo.logoDataUrl);
    } else if (logo) {
      toDataUrl(logo).then(setFacilityLogo);
    }
  }, [combinedInvoice]);

  const getFacilityLogo = async () => {
    try {
      const facilityFromState = routerLocation.state?.facility;

      if (facilityFromState?.logo) {
        const fullLogoUrl = facilityFromState.logo;
        localStorage.setItem("selectedFacilityLogo", fullLogoUrl);
        setFacilityLogo(fullLogoUrl);
      } else {
        const storedLogo = localStorage.getItem("selectedFacilityLogo");
        if (storedLogo) {
          setFacilityLogo(storedLogo);
        } else {
          setFacilityLogo("");
        }
      }
    } catch (error) {
      console.error("Error fetching facility logo:", error);
      setFacilityLogo("");
    }
  };

  // Fetch company details
  const fetchCompanyDetails = async () => {
    try {
      const response = await makeRequest2(
        `${getCompanyInformationURL}/${facilityId}`,
        "GET",
      );
      if (response.success) {
        setCompanyDetails(response.data);
      } else {
        toastify(
          response.message || "Failed to fetch company details",
          "error",
        );
      }
    } catch (error) {
      toastify(error.message, "error");
    }
  };

  // Fetch combined invoice data
  const fetchCombinedInvoiceData = async () => {
    setLoading(true);
    const facilityId = await getItem("selectedFacilityId");
    try {
      // Update this URL to match your API endpoint for combined invoices
      const response = await makeRequest2(
        `/api/app/combined_invoices/get_single_invoice/${facilityId}/${combinedInvoiceId}`,
        "GET",
      );

      if (response.success) {
        const invoiceData = response.data?.data || response.data;
        setCombinedInvoice(invoiceData);
      } else {
        toastify(response.message || "Failed to fetch combined invoice", "error");
      }
    } catch (error) {
      toastify(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to safely extract data
  const extractResponseData = (response) => {
    if (!response) return null;

    if (response.success && response.data) {
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      return response.data;
    }

    return response;
  };

  // Fetch payment details for combined invoice
  const fetchPaymentDetails = async () => {
    try {
      setBillerAddressLoading(true);
      setBankDetailsLoading(true);
      setPaymentSettingsLoading(true);

      // Reset previous data
      setBankDetails(null);
      setBillerAddress(null);
      setPaymentSettings(null);
      setPaymentMethod(null);

      // Process payment data from combined invoice
      await processPaymentData();
    } catch (error) {
      console.error("Error fetching payment details:", error);
      toastify("Error fetching payment details", "error");
      setBillerAddressLoading(false);
      setBankDetailsLoading(false);
      setPaymentSettingsLoading(false);
    }
  };

  const processPaymentData = async () => {
    try {
      console.log("Processing combined invoice payment data:", combinedInvoice);

      const promises = [];
      let hasBillerAddress = false;
      let hasPaymentMethod = false;

      // Check for biller address in combined invoice
      if (combinedInvoice.billerAddressId) {
        hasBillerAddress = true;
        promises.push(fetchBillerAddress(combinedInvoice.billerAddressId));
      } else {
        console.log("No biller address found in combined invoice");
        setBillerAddressLoading(false);
      }

      // Check payment method from combined invoice
      if (combinedInvoice.paymentMethod?.type) {
        hasPaymentMethod = true;

        const paymentMethodType = combinedInvoice.paymentMethod.type;
        console.log("Combined invoice payment method:", paymentMethodType);

        if (paymentMethodType === "Bank Transfer") {
          // Check if bank details are already embedded in the response
          if (combinedInvoice.bankDetails) {
            setBankDetails(combinedInvoice.bankDetails);
            setPaymentMethod("bank");
            setBankDetailsLoading(false);
            setPaymentSettingsLoading(false);
          } else if (combinedInvoice.paymentMethod.bankDetailsId) {
            console.log("Fetching bank details for:", combinedInvoice.paymentMethod.bankDetailsId);
            promises.push(fetchBankDetails(combinedInvoice.paymentMethod.bankDetailsId));
            setPaymentMethod("bank");
          } else {
            console.log("Bank Transfer selected but no bank details available");
            setBankDetailsLoading(false);
            setPaymentSettingsLoading(false);
          }
        } else if (paymentMethodType === "Mobile Money" || paymentMethodType === "M-Pesa") {
          // Check if mobile money details are embedded
          if (combinedInvoice.paymentMethod.mobileMoneyDetails) {
            console.log("Using embedded mobile money details:", combinedInvoice.paymentMethod.mobileMoneyDetails);
            setPaymentSettings(combinedInvoice.paymentMethod.mobileMoneyDetails);
            setPaymentMethod("mobile");
            setBankDetailsLoading(false);
            setPaymentSettingsLoading(false);
          } else if (combinedInvoice.paymentMethod.mobileMoneyDetails?.businessNumber) {
            // Use the business number directly
            console.log("Using business number from combined invoice:", combinedInvoice.paymentMethod.mobileMoneyDetails.businessNumber);
            setPaymentSettings({
              shortCode: combinedInvoice.paymentMethod.mobileMoneyDetails.businessNumber,
            });
            setPaymentMethod("mobile");
            setBankDetailsLoading(false);
            setPaymentSettingsLoading(false);
          } else {
            console.log("Mobile Money selected but no business number available");
            setBankDetailsLoading(false);
            setPaymentSettingsLoading(false);
          }
        } else {
          console.log("Payment type set to:", paymentMethodType);
          // For Cash, Cheque, or Other - just set payment method but no details needed
          setPaymentMethod(paymentMethodType.toLowerCase());
          setBankDetailsLoading(false);
          setPaymentSettingsLoading(false);
        }
      } else {
        console.log("No payment method found in combined invoice");
      }

      // If no biller address was found, ensure its loading state is false
      if (!hasBillerAddress) {
        setBillerAddressLoading(false);
      }

      // If no payment method was found, ensure loading states are false
      if (!hasPaymentMethod) {
        console.log("No payment method found, setting loading states to false");
        setBankDetailsLoading(false);
        setPaymentSettingsLoading(false);
      }

      // Wait for all promises to settle (if any)
      if (promises.length > 0) {
        console.log("Waiting for", promises.length, "promises to settle");
        const results = await Promise.allSettled(promises);
        console.log("Promise results:", results);

        // Check if any promises were rejected
        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            console.error(`Promise ${index} was rejected:`, result.reason);
          }
        });
      } else {
        console.log("No promises to wait for - all data was embedded or no payment method configured");
      }

    } catch (error) {
      console.error("Error processing payment configuration:", error);
      toastify("Error processing payment configuration: " + error.message, "error");
      setBillerAddressLoading(false);
      setBankDetailsLoading(false);
      setPaymentSettingsLoading(false);
    }
  };

  // Fetch bank details
  const fetchBankDetails = async (bankAccountId) => {
    try {
      console.log("Fetching bank details from URL:", `${getBankDetailsForLevyURL}/${facilityId}/${bankAccountId}`);

      const response = await makeRequest2(
        `${getBankDetailsForLevyURL}/${facilityId}/${bankAccountId}`,
        "GET",
      );

      console.log("Bank details response:", response);

      if (response.success) {
        const bankData = extractResponseData(response);
        console.log("Extracted bank data:", bankData);
        setBankDetails(bankData);
        setPaymentMethod("bank");
      } else {
        console.error("Failed to fetch bank details:", response);
        toastify("Failed to fetch bank details: " + (response.message || "Unknown error"), "error");
      }
    } catch (error) {
      console.error("Error fetching bank details:", error);
      toastify("Error fetching bank details: " + error.message, "error");
    } finally {
      console.log("Setting bankDetailsLoading to false");
      setBankDetailsLoading(false);
      setPaymentSettingsLoading(false);
    }
  };

  const fetchBillerAddress = async (billerAddressId) => {
    try {
      console.log("Fetching biller address from URL:", `${getBillerAddressForLevyURL}/${facilityId}/${billerAddressId}`);

      const response = await makeRequest2(
        `${getBillerAddressForLevyURL}/${facilityId}/${billerAddressId}`,
        "GET",
      );

      console.log("Biller address response:", response);

      if (response.success) {
        const billerData = extractResponseData(response);
        console.log("Extracted biller data:", billerData);
        setBillerAddress(billerData);
      } else {
        console.error("Failed to fetch biller address:", response);
        toastify("Failed to fetch biller address: " + (response.message || "Unknown error"), "error");
      }
    } catch (error) {
      console.error("Error fetching biller address:", error);
      toastify("Error fetching biller address: " + error.message, "error");
    } finally {
      console.log("Setting billerAddressLoading to false");
      setBillerAddressLoading(false);
    }
  };

  // Mark invoice as read
  const markInvoiceAsRead = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `/api/combined-invoices/${facilityId}/${combinedInvoiceId}/markAsRead`,
        "POST",
      );

      if (response.success) {
        setCombinedInvoice((prev) => ({
          ...prev,
          viewStatus: {
            ...prev.viewStatus,
            isOpened: true,
            openedAt: new Date(),
          },
        }));
        toastify("Invoice marked as read", "success");
      } else {
        toastify(response.message || "Failed to mark invoice as read", "error");
      }
    } catch (error) {
      toastify("Error marking invoice as read", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle PDF download 
  const handleDownloadPDF = async () => {
    const element = document.getElementById("printable-invoice");
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const filename = `Combined_Invoice_${combinedInvoice?.period}_${timestamp}.pdf`;

    try {
      setLoading(true);
      toastify("Generating PDF...", "info");

      const scale = 2; // render at higher resolution for crisp text/logo
      const canvas = await html2canvas(element, {
        scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/jpeg", 1.0);

      // Convert the ACTUAL rendered element size (not the upscaled canvas
      // size) to mm, so the PDF page matches the invoice's real dimensions.
      // 1px = 0.264583mm at 96dpi.
      const pxToMm = 0.264583;
      const imgWidthMm = (canvas.width / scale) * pxToMm;
      const imgHeightMm = (canvas.height / scale) * pxToMm;

      // Custom page size = content size => always exactly one page, no
      // matter how long the combined invoice is.
      const pdf = new jsPDF({
        orientation: imgWidthMm > imgHeightMm ? "landscape" : "portrait",
        unit: "mm",
        format: [imgWidthMm, imgHeightMm],
      });

      pdf.addImage(imgData, "JPEG", 0, 0, imgWidthMm, imgHeightMm, "", "FAST");
      pdf.save(filename);

      toastify("PDF downloaded successfully!", "success");
    } catch (error) {
      console.error("PDF generation error:", error);
      toastify("Failed to generate PDF. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle cash payment success
  const handleCashPaymentSuccess = (payment) => {
    fetchCombinedInvoiceData();
  };

  // Helper functions
  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString();
  };

  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return "0.00";
    const currencyCode = combinedInvoice?.currency?.code || "KES";

    const numericAmount = parseFloat(amount);
    const formattedNumber = numericAmount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return `${currencyCode} ${formattedNumber}`;
  };

  // Calculate subtotal from individual invoice amounts
  const calculateSubtotal = () => {
    if (!combinedInvoice?.invoices) return 0;
    return combinedInvoice.invoices.reduce((sum, invoice) => {
      return sum + (parseFloat(invoice.amount) || 0);
    }, 0);
  };

  // Calculate total tax from individual invoice taxes
  const calculateTotalTax = () => {
    if (!combinedInvoice?.invoices) return 0;
    return combinedInvoice.invoices.reduce((sum, invoice) => {
      return sum + (parseFloat(invoice.tax) || 0);
    }, 0);
  };

  // Calculate total due
  const calculateTotalDue = () => {
    if (!combinedInvoice) return 0;

    const subtotal = calculateSubtotal();
    const totalTax = calculateTotalTax();
    const totalBalanceBroughtForward = parseFloat(combinedInvoice.totalBalanceBroughtForward) || 0;
    const amountPaid = parseFloat(combinedInvoice.amountPaid) || 0;

    return subtotal + totalTax + totalBalanceBroughtForward - amountPaid;
  };

  // Check if final amount is credit or debit
  const isFinalCredit = calculateTotalDue() < 0;

  // Define read/unread status styles
  const readStatusStyles = {
    button: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "0.5rem 1rem",
      borderRadius: "20px",
      fontSize: "0.875rem",
      fontWeight: "600",
      border: "none",
      cursor: "pointer",
      transition: "all 0.2s ease",
      marginLeft: "0.75rem",
    },
    unread: {
      backgroundColor: "#ffc107",
      color: "#212529",
    },
    read: {
      backgroundColor: "#28a745",
      color: "#ffffff",
    },
  };

  // Add loading indicators in the action bar similar to lease invoice
  const renderLoadingIndicators = () => {
    if (!combinedInvoice || allPaymentDataLoaded) return null;

    return (
      <div style={{ display: "inline-block", marginLeft: "20px" }}>
        {billerAddressLoading && (
          <span className="text-muted">
            <i className="fas fa-spinner fa-spin me-1"></i>
            Loading biller address...
          </span>
        )}
        {(bankDetailsLoading || paymentSettingsLoading) && (
          <span className="text-muted ms-2">
            <i className="fas fa-spinner fa-spin me-1"></i>
            Loading payment details...
          </span>
        )}
      </div>
    );
  };

  // Render loading state
  if (loading && !combinedInvoice) {
    return (
      <Layout>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "70vh" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="ms-2">Loading combined invoice details...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={styles.pageContainer}>
        {/* Action Bar with Dropdown and Read/Unread Status */}
        <div className={styles.actionCard}>
          <div className={styles.actionCardLeft}>
            <Link
              to="#"
              onClick={() => navigate(-1)}
              className={styles.backLink}
            >
              <i className="ti ti-arrow-narrow-left"></i>
              <span>Back</span>
            </Link>

            {/* Read/Unread Status Button */}
            <div style={{ display: "inline-block", marginLeft: "20px" }}>
              {combinedInvoice?.viewStatus?.isOpened === true ? (
                <button
                  style={{
                    ...readStatusStyles.button,
                    ...readStatusStyles.read,
                  }}
                >
                  <i className="fas fa-check-circle me-1"></i>
                  Read
                </button>
              ) : (
                <button
                  style={{
                    ...readStatusStyles.button,
                    ...readStatusStyles.unread,
                  }}
                  onClick={markInvoiceAsRead}
                >
                  <i className="fas fa-exclamation-circle me-1"></i>
                  Unread
                </button>
              )}
            </div>

            {/* Loading indicators */}
            {renderLoadingIndicators()}
          </div>
          <div className={styles.actionCardRight}>
            <CombinedInvoiceActionsDropdown
              invoice={combinedInvoice}
              facilityId={facilityId}
              handleDownloadPDF={handleDownloadPDF}
              onCashPaymentSuccess={handleCashPaymentSuccess}
              onInvoiceVoided={fetchCombinedInvoiceData}
              loading={loading}
            />
          </div>
        </div>

        {/* Data Status Messages - Similar to lease invoice */}
        {allPaymentDataLoaded && (
          <div className="mb-3">
            {!bankDetails && !paymentSettings && combinedInvoice?.paymentMethod?.type && (
              <div className="alert alert-warning" role="alert">
                <i className="fas fa-exclamation-triangle me-2"></i>
                Payment method configured but no payment details available.
              </div>
            )}
          </div>
        )}

        {/* Show loading indicators */}
        {!allPaymentDataLoaded && (
          <div className="mb-3">
            {billerAddressLoading && (
              <div className="alert alert-info" role="alert">
                <i className="fas fa-spinner fa-spin me-2"></i>
                Loading biller address...
              </div>
            )}
            {(bankDetailsLoading || paymentSettingsLoading) && (
              <div className="alert alert-info" role="alert">
                <i className="fas fa-spinner fa-spin me-2"></i>
                Loading payment details...
              </div>
            )}
          </div>
        )}

        {/* Invoice Content */}
        <div className={styles.invoiceContainer}>
          <div id="printable-invoice" className={styles.invoiceContent}>
            {/* Invoice Status Banner */}
            <div
              className={styles.statusBanner}
              data-status={combinedInvoice?.status?.toLowerCase()}
            >
              <span>{combinedInvoice?.status}</span>
            </div>

            <div className={styles.invoiceHeader}>
              <div className={styles.invoiceHeaderContent}>
                <div className={styles.headerLeft}>
                  <h1
                    className={styles.invoiceTitleInHeader}
                    style={{
                      marginTop: "-20px",
                      marginLeft: "60%",
                      color: "white",
                      fontSize: "28px",
                      fontWeight: "600",
                    }}
                  >
                    COMBINED INVOICE
                  </h1>
                  {facilityLogo && (
                    <div
                      className={styles.logoContainer}
                      style={{ marginTop: "0", marginLeft: "60%" }}
                    >
                      <img
                        src={facilityLogo}
                        alt="Company Logo"
                        className={styles.companyLogo}
                      />
                    </div>
                  )}
                </div>
                <div className={styles.headerDetails}>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>PERIOD</span>
                    <span className={styles.value}>
                      {combinedInvoice?.period}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>INVOICE NO.</span>
                    <span className={styles.value}>
                      {combinedInvoice?.combinedInvoiceNumber}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>ACCOUNT NO.</span>
                    <span className={styles.value}>
                      {combinedInvoice?.customer?.accountNumber}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>ISSUE DATE</span>
                    <span className={styles.value}>
                      {formatDate(combinedInvoice?.issueDate)}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>DUE DATE</span>
                    <span className={styles.value}>
                      {formatDate(combinedInvoice?.dueDate)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.invoiceBody}>
              <div className={styles.addressSection}>
                <div className={styles.addressLayout}>
                  <div className={styles.fromSection}>
                    <h2 className={styles.sectionTitle}>FROM</h2>
                    <div className={styles.addressContent}>
                      {billerAddress ? (
                        <>
                          {billerAddress.companyName && (
                            <div>{billerAddress.companyName}</div>
                          )}
                          {billerAddress.address && (
                            <div>{billerAddress.address}</div>
                          )}
                          {billerAddress.postalCode && (
                            <div>{billerAddress.postalCode}</div>
                          )}
                          {(billerAddress.city || billerAddress.country) && (
                            <div>
                              {billerAddress.city}
                              {billerAddress.city && billerAddress.country
                                ? ", "
                                : ""}
                              {billerAddress.country}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div>{companyDetails?.name || "Company Name"}</div>
                          <div>{companyDetails?.address || "Company Address"}</div>
                          <div>{companyDetails?.country || "Country"}</div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className={styles.billToSection}>
                    <h2 className={styles.sectionTitle}>BILL TO</h2>
                    <div className={styles.addressContent}>
                      <div>{`${combinedInvoice?.customer?.firstName || ""} ${combinedInvoice?.customer?.lastName || ""}`}</div>
                      <div>Unit: {combinedInvoice?.unit?.name || "N/A"}</div>
                    </div>
                  </div>
                  <div className={styles.totalSection}>
                    <h2 className={styles.sectionTitle}>TOTAL DUE</h2>
                    <div
                      className={`${styles.totalAmount} ${isFinalCredit ? styles.creditAmount : ""}`}
                    >
                      {isFinalCredit
                        ? formatAmount(-Math.abs(calculateTotalDue()))
                        : formatAmount(calculateTotalDue())}
                    </div>
                  </div>
                </div>
              </div>

              {/* Individual Invoices Section */}
              <div className={`${styles.itemsGrid} ${styles.combinedInvoiceGrid}`}>
                <div className={styles.gridHeader}>
                  <span className={styles.columnHeader}>INVOICE NO.</span>
                  <span className={styles.columnHeader}>DESCRIPTION</span>
                  <span className={`${styles.columnHeader} ${styles.right}`}>
                    AMOUNT
                  </span>
                </div>

                {combinedInvoice?.invoices?.map((invoice, index) => (
                  <div key={index} className={styles.gridRow}>
                    <span className={styles.value}>{invoice.invoiceNumber}</span>
                    <span className={styles.value}>{invoice.description}</span>
                    <span className={`${styles.value} ${styles.right}`}>
                      {formatAmount(invoice.amount)}
                    </span>
                  </div>
                ))}

                <div className={styles.calculations}>
                  <div className={styles.calculationsGrid}>
                    {/* Subtotal (sum of individual invoice amounts without tax) */}
                    <div className={styles.calculationsRow}>
                      <span className={styles.calcLabel}>SUBTOTAL</span>
                      <span className={styles.calcValue}>
                        {formatAmount(calculateSubtotal())}
                      </span>
                    </div>

                    {/* Tax (sum of individual invoice taxes) */}
                    <div className={styles.calculationsRow}>
                      <span className={styles.calcLabel}>
                        TAX
                      </span>
                      <span className={styles.calcValue}>
                        {formatAmount(calculateTotalTax())}
                      </span>
                    </div>

                    {/* Invoice Total (Subtotal + Tax) */}
                    <div className={styles.calculationsRow}>
                      <span className={styles.calcLabel}>INVOICE TOTAL</span>
                      <span className={styles.calcValue}>
                        {formatAmount(calculateSubtotal() + calculateTotalTax())}
                      </span>
                    </div>

                    {/* Divider */}
                    <div className={styles.divider}></div>

                    {/* Balance Brought Forward */}
                    {combinedInvoice?.totalBalanceBroughtForward !== 0 && (
                      <div className={styles.calculationsRow}>
                        <span className={styles.calcLabel}>
                          BALANCE B/FORWARD
                        </span>
                        <span
                          className={`${styles.calcValue} ${combinedInvoice?.totalBalanceBroughtForward < 0
                              ? styles.creditAmount
                              : styles.debitAmount
                            }`}
                        >
                          {combinedInvoice?.totalBalanceBroughtForward < 0
                            ? "(" + formatAmount(Math.abs(combinedInvoice?.totalBalanceBroughtForward)) + ")"
                            : formatAmount(Math.abs(combinedInvoice?.totalBalanceBroughtForward))}
                        </span>
                      </div>
                    )}

                    {/* Amount Payable */}
                    <div className={styles.calculationsRow}>
                      <span className={styles.calcLabel}>AMOUNT PAYABLE</span>
                      <span className={styles.calcValue}>
                        {formatAmount(
                          calculateSubtotal() +
                          calculateTotalTax() +
                          (combinedInvoice?.totalBalanceBroughtForward || 0)
                        )}
                      </span>
                    </div>

                    {/* Divider */}
                    <div className={styles.divider}></div>

                    {/* Amount Paid */}
                    <div className={styles.calculationsRow}>
                      <span className={styles.calcLabel}>PAYMENT</span>
                      <span className={styles.calcValue}>
                        {formatAmount(combinedInvoice?.amountPaid)}
                      </span>
                    </div>

                    {/* Total Due */}
                    <div
                      className={`${styles.calculationsRow} ${styles.balance}`}
                    >
                      <span className={styles.calcLabel}>TOTAL DUE</span>
                      <span
                        className={`${styles.calcValue} ${isFinalCredit ? styles.creditAmount : styles.debitAmount
                          }`}
                      >
                        {isFinalCredit
                          ? formatAmount(-Math.abs(calculateTotalDue()))
                          : formatAmount(calculateTotalDue())}
                      </span>
                    </div>
                  </div>

                  <div className={styles.totalRow}>
                    <span className={styles.totalLabel}>TOTAL DUE</span>
                    <span
                      className={`${styles.totalValue} ${isFinalCredit ? styles.creditAmount : styles.debitAmount
                        }`}
                    >
                      {isFinalCredit
                        ? "(" + formatAmount(Math.abs(calculateTotalDue())) + ")"
                        : formatAmount(calculateTotalDue())}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Details Footer */}
              {allPaymentDataLoaded ? (
                paymentMethod === "mobile" && paymentSettings ? (
                  <div
                    className={styles.footer}
                    style={{
                      backgroundColor: "#f0f0f0",
                      padding: "12px 20px",
                      marginLeft: "-20px",
                      marginRight: "-20px",
                      marginBottom: "-20px",
                      borderBottomLeftRadius: "8px",
                      borderBottomRightRadius: "8px",
                    }}
                  >
                    <div className="row align-items-center">
                      <div className="col-sm-6">
                        <div
                          className={styles.paymentDetails}
                          style={{ marginBottom: 0 }}
                        >
                          <p
                            style={{
                              fontSize: "12px",
                              fontWeight: "bold",
                              marginBottom: "4px",
                            }}
                          >
                            Payment Details:
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: "2px" }}>
                            <strong>Bank:</strong> Mpesa
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: 0 }}>
                            <strong>Paybill:</strong>{" "}
                            {paymentSettings?.shortCode || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div className="col-sm-6 text-center">
                        <img
                          src="/signature.png"
                          alt="Digital Signature"
                          style={{
                            width: "120px",
                            height: "45px",
                            objectFit: "contain",
                          }}
                        />
                        <p
                          style={{
                            textAlign: "center",
                            marginTop: "2px",
                            marginBottom: "2px",
                            fontSize: "10px",
                          }}
                        >
                          ___________________________
                        </p>
                        <p
                          style={{
                            textAlign: "center",
                            fontSize: "9px",
                            marginBottom: 0,
                          }}
                        >
                          Authorized Signature
                        </p>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 pt-1">
                        <p
                          style={{
                            textAlign: "center",
                            fontSize: "11px",
                            marginBottom: 0,
                          }}
                        >
                          To complete your payment, use paybill
                          <strong>
                            {" "}
                            {paymentSettings?.shortCode || "N/A"}{" "}
                          </strong>
                          with account number
                          <strong>
                            {" "}
                            {combinedInvoice?.customer?.accountNumber || "N/A"}
                          </strong>
                          .
                        </p>
                      </div>
                    </div>
                  </div>
                ) : paymentMethod === "bank" && bankDetails ? (
                  <div
                    className={styles.footer}
                    style={{
                      backgroundColor: "#f0f0f0",
                      padding: "12px 20px",
                      marginLeft: "-20px",
                      marginRight: "-20px",
                      marginBottom: "-20px",
                      borderBottomLeftRadius: "8px",
                      borderBottomRightRadius: "8px",
                    }}
                  >
                    <div className="row">
                      <div className="col-12 text-center pb-2">
                        <h6
                          className={styles.detailsTitle}
                          style={{
                            margin: "0 auto",
                            fontWeight: "bold",
                            fontSize: "12px",
                          }}
                        >
                          PAYMENT DETAILS
                        </h6>
                      </div>
                    </div>

                    <div className="row align-items-center">
                      <div className="col-sm-4">
                        <div
                          className={styles.paymentDetails}
                          style={{ marginBottom: 0 }}
                        >
                          <p style={{ fontSize: "11px", marginBottom: "2px" }}>
                            <strong>Bank:</strong>{" "}
                            {bankDetails?.bankName || "N/A"}
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: "2px" }}>
                            <strong>Account:</strong>{" "}
                            {bankDetails?.accountName || "N/A"}
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: 0 }}>
                            <strong>Acc No:</strong>{" "}
                            {bankDetails?.accountNumber || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div className="col-sm-3">
                        <div
                          className={styles.additionalDetails}
                          style={{ marginBottom: 0 }}
                        >
                          <p style={{ fontSize: "11px", marginBottom: "2px" }}>
                            <strong>Branch:</strong>{" "}
                            {bankDetails?.branchName || "N/A"}
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: "2px" }}>
                            <strong>Code:</strong>{" "}
                            {bankDetails?.branchCode || "N/A"}
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: 0 }}>
                            <strong>Swift:</strong>{" "}
                            {bankDetails?.bankCode || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div className="col-sm-5 text-center">
                        <img
                          src="/signature.png"
                          alt="Digital Signature"
                          style={{
                            width: "120px",
                            height: "45px",
                            objectFit: "contain",
                          }}
                        />
                        <p
                          style={{
                            marginTop: "0px",
                            marginBottom: "2px",
                            fontSize: "10px",
                          }}
                        >
                          ___________________________
                        </p>
                        <p style={{ fontSize: "9px", marginBottom: 0 }}>
                          Authorized Signature
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className={styles.footer}
                    style={{
                      backgroundColor: "#f0f0f0",
                      padding: "12px 20px",
                      marginLeft: "-20px",
                      marginRight: "-20px",
                      marginBottom: "-20px",
                      borderBottomLeftRadius: "8px",
                      borderBottomRightRadius: "8px",
                    }}
                  >
                    <div className="row align-items-center justify-content-center">
                      {combinedInvoice?.paymentMethod?.type ? (
                        <div className="col-12 text-center">
                          <div className={styles.noPaymentMethod}>
                            <i
                              className="fas fa-exclamation-triangle text-warning"
                              style={{
                                fontSize: "1.5rem",
                                marginBottom: "0.5rem",
                              }}
                            ></i>
                            <p
                              className="text-muted mb-0"
                              style={{ fontSize: "12px" }}
                            >
                              Payment Method: {combinedInvoice.paymentMethod.type} (No details available)
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="col-auto text-center">
                          <p
                            style={{
                              marginTop: "5px",
                              marginBottom: "2px",
                              fontSize: "10px",
                            }}
                          >
                            ___________________________
                          </p>
                          <p
                            className="text-muted"
                            style={{ fontSize: "9px", marginBottom: 0 }}
                          >
                            Authorized Signature
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )
              ) : (
                <div
                  className={styles.footer}
                  style={{
                    backgroundColor: "#f8f9fa",
                    borderTop: "2px solid #dee2e6",
                  }}
                >
                  <div className="row px-5 py-4">
                    <div className="col-12 text-center">
                      <div className={styles.loadingPaymentDetails}>
                        <div
                          className="spinner-border text-primary mb-3"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <h5 className="text-muted">
                          Loading Payment Details
                        </h5>
                        <p className="text-muted mb-0">
                          Please wait while we fetch the payment information...
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CombinedInvoicePage;